import { Icon } from '@iconify/react';

const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BulkImportValidation = ({ validateResult, onNext, onBack, loading }) => {
    if (!validateResult) return null;

    const { totalJournalGroups, validCount, errorCount, successRows = [], errors = {} } = validateResult;
    const { rowErrors = [], groupErrors = [] } = errors;
    const allErrors = [...rowErrors, ...groupErrors];

    return (
        <div>
            {/* Summary Bar */}
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-3 tw-mb-3">
                <div className="tw-flex tw-items-center tw-gap-5">
                    <div className="tw-flex tw-items-center tw-gap-1.5">
                        <Icon icon="mdi:file-document-multiple-outline" className="tw-text-blue-500 tw-text-sm" />
                        <span className="tw-text-xs tw-text-gray-600">Total Groups: <strong className="tw-text-gray-800">{totalJournalGroups}</strong></span>
                    </div>
                    <div className="tw-w-px tw-h-5 tw-bg-gray-200" />
                    <div className="tw-flex tw-items-center tw-gap-1.5">
                        <Icon icon="mdi:check-circle-outline" className="tw-text-emerald-500 tw-text-sm" />
                        <span className="tw-text-xs tw-text-gray-600">Valid: <strong className="tw-text-emerald-600">{validCount}</strong></span>
                    </div>
                    <div className="tw-w-px tw-h-5 tw-bg-gray-200" />
                    <div className="tw-flex tw-items-center tw-gap-1.5">
                        <Icon icon="mdi:alert-circle-outline" className="tw-text-red-500 tw-text-sm" />
                        <span className="tw-text-xs tw-text-gray-600">Errors: <strong className="tw-text-red-600">{errorCount}</strong></span>
                    </div>
                </div>
            </div>

            {/* Valid Entries Table */}
            {successRows.length > 0 && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden tw-mb-3">
                    <div className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-1.5">
                        <Icon icon="mdi:check-circle-outline" className="tw-text-emerald-500 tw-text-sm" />
                        <h4 className="tw-text-xs tw-font-semibold tw-text-gray-800">Valid Entries ({validCount})</h4>
                    </div>
                    <div className="tw-overflow-x-auto">
                        <table className="tw-w-full tw-text-xs">
                            <thead>
                                <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Voucher ID</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Date</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Type</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Remarks</th>
                                    <th className="tw-text-center tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Debits</th>
                                    <th className="tw-text-center tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Credits</th>
                                    <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Amount</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Rows</th>
                                </tr>
                            </thead>
                            <tbody>
                                {successRows.map((row, idx) => (
                                    <tr key={idx} className="tw-border-b tw-border-gray-50">
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-700 tw-font-medium">{row.voucher_id}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-700">{row.date}</td>
                                        <td className="tw-px-3 tw-py-2">
                                            <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium tw-bg-blue-50 tw-text-blue-600">
                                                {row.entry_type}
                                            </span>
                                        </td>
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-600 tw-max-w-[200px] tw-truncate">{row.remarks || '-'}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-center tw-text-gray-600">{row.debit_count}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-center tw-text-gray-600">{row.credit_count}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-right tw-text-emerald-600 tw-font-medium">₹{formatCurrency(row.total_amount)}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-400 tw-text-[10px]">{row.rows?.join(', ')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Error Entries */}
            {allErrors.length > 0 && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden tw-mb-3">
                    <div className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-1.5">
                        <Icon icon="mdi:alert-outline" className="tw-text-red-500 tw-text-sm" />
                        <h4 className="tw-text-xs tw-font-semibold tw-text-gray-800">Errors ({allErrors.length})</h4>
                    </div>
                    <div className="tw-overflow-x-auto">
                        <table className="tw-w-full tw-text-xs">
                            <thead>
                                <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Row</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Voucher ID</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Type</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Errors</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allErrors.map((err, idx) => (
                                    <tr key={idx} className="tw-border-b tw-border-gray-50 tw-bg-red-50/50">
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-500">{err.rowNum || err.rows?.join(', ') || '-'}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-700">{err.voucher_id || '-'}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-400 tw-text-[10px]">
                                            {err.rows ? 'Group' : 'Row'}
                                        </td>
                                        <td className="tw-px-3 tw-py-2">
                                            <div className="tw-flex tw-flex-wrap tw-gap-1">
                                                {(err.errors || []).map((e, i) => (
                                                    <span key={i} className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[9px] tw-font-medium tw-bg-red-100 tw-text-red-600">
                                                        {e}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Empty State */}
            {successRows.length === 0 && allErrors.length === 0 && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-8">
                    <Icon icon="mdi:table-off" className="tw-text-3xl tw-text-gray-300 tw-mx-auto" />
                    <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No data found in the uploaded file</p>
                </div>
            )}

            {/* Action Buttons */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mt-4">
                <button
                    onClick={onBack}
                    disabled={loading}
                    className="tw-flex tw-items-center tw-gap-1 tw-px-4 tw-py-2 tw-rounded-lg tw-text-xs tw-font-semibold tw-text-gray-500 tw-bg-gray-100 hover:tw-bg-gray-200 disabled:tw-opacity-50 tw-transition-colors"
                >
                    <Icon icon="mdi:arrow-left" className="tw-text-sm" />
                    Back
                </button>
                <button
                    onClick={onNext}
                    disabled={validCount === 0 || loading}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-5 tw-py-2 tw-rounded-lg tw-text-xs tw-font-semibold tw-text-white tw-bg-red-600 hover:tw-bg-red-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
                >
                    {loading && <Icon icon="mdi:loading" className="tw-text-sm tw-animate-spin" />}
                    <Icon icon="mdi:upload-outline" className="tw-text-sm" />
                    {loading ? 'Importing...' : `Import ${validCount} Valid ${validCount === 1 ? 'Entry' : 'Entries'}`}
                </button>
            </div>
        </div>
    );
};

export default BulkImportValidation;
